import { ScratchCardConfig } from '@shared/models';
import { loadImage, mousePosition } from '@shared/utils';

export class ScratchCard {
  private canvas: HTMLCanvasElement;
  private context: CanvasRenderingContext2D;

  private document: Document;
  private element: HTMLElement;
  private config: ScratchCardConfig;

  private readonly defaultConfig = {
    width: 250,
    height: 120,
    zoneRadius: 24,
    mask: '#ffce00',
    maskType: 'color',
    percentToFinish: 50,
    canvasClass: 'canvas',
  };

  private isDragging = false;
  private callBackHandled = false;

  constructor(document: Document, cnf: ScratchCardConfig) {
    this.config = {
      ...this.defaultConfig,
      ...cnf,
    };

    this.document = document;
    this.element = cnf.element;

    this.callBackHandled = false;

    this.generateCanvas();

    this.context = this.canvas.getContext('2d', {
      willReadFrequently: true,
    }) as CanvasRenderingContext2D;

    this.fillCanvas();
  }

  init(): void {
    this.addEventListeners();
  }

  private generateCanvas(): void {
    // create canvas element
    this.canvas = this.document.createElement('canvas');
    this.canvas.classList.add(this.config.canvasClass);
    this.canvas.width = this.config.width;
    this.canvas.height = this.config.height;

    // Add canvas into container
    this.element.appendChild(this.canvas);
  }

  private fillCanvas(): void {
    switch (this.config.maskType) {
      case 'color':
        this.context.fillStyle = this.config.mask;
        this.context.fillRect(0, 0, this.config.width, this.config.height);
        this.config.maskLoadedCallback();
        break;

      case 'image':
        loadImage(this.config.mask)
          .then((src: HTMLImageElement) => {
            this.context.drawImage(
              src,
              0,
              0,
              this.canvas.width,
              this.canvas.height
            );
            this.config.maskLoadedCallback();
          })
          .catch((error) => {
            console.error('Failed to load scratch card mask image:', error);
            // Fallback: draw a solid-color mask so the canvas is still usable
            this.context.fillStyle = this.defaultConfig.mask;
            this.context.fillRect(0, 0, this.config.width, this.config.height);
            this.config.maskLoadedCallback();
          });
        break;

      default:
        console.error('mask type must be of type image or color');
        break;
    }
  }

  private addEventListeners(): void {
    // Desktop Events
    this.canvas.addEventListener('mousedown', (event) => {
      this.isDragging = true;
      this.scratch(event.offsetX, event.offsetY);
    });

    this.canvas.addEventListener('mousemove', (event) => {
      if (this.isDragging) {
        this.scratch(event.offsetX, event.offsetY);
      }
    });

    this.canvas.addEventListener('mouseup', () => {
      this.isDragging = false;
    });

    this.canvas.addEventListener('mouseleave', () => {
      this.isDragging = false;
    });

    // Mobile Events
    this.canvas.addEventListener('touchstart', (event) => {
      this.isDragging = true;
      const position = mousePosition(event, this.element);
      this.scratch(position[0], position[1]);
    });

    this.canvas.addEventListener('touchmove', (event) => {
      if (this.isDragging) {
        const position = mousePosition(event, this.element);
        this.scratch(position[0], position[1]);
      }
    });

    this.canvas.addEventListener('touchend', () => {
      this.isDragging = false;
    });
  }

  private scratch(x: number, y: number): void {
    this.context.globalCompositeOperation = 'destination-out';
    this.context.beginPath();
    this.context.arc(x, y, this.config.zoneRadius, 0, 2 * Math.PI);
    this.context.fill();

    // Get Scratch Percent
    const percent = this.updatePercent();

    if (percent >= this.config.percentToFinish && !this.callBackHandled) {
      this.clearCanvas();
      this.config.callback();
      this.callBackHandled = true;
    }
  }

  private updatePercent(): number {
    let counter = 0; // number of pixels cleared
    const imageData = this.context.getImageData(
      0,
      0,
      this.canvas.width,
      this.canvas.height
    );

    // loop data image drop every 4 items [r, g, b, a, ...]
    // Red: image.data[0]
    // Green: image.data[1]
    // Blue: image.data[2]
    // Alpha: image.data[3]
    for (let i = 0; i < imageData.data.length; i += 4) {
      // Increment the counter only if the pixel is completely cleared
      if (
        imageData.data[i] === 0 &&
        imageData.data[i + 1] === 0 &&
        imageData.data[i + 2] === 0 &&
        imageData.data[i + 3] === 0
      ) {
        counter++;
      }
    }

    return counter >= 1
      ? (counter / (this.canvas.width * this.canvas.height)) * 100
      : 0;
  }

  private clearCanvas(): void {
    this.context.clearRect(0, 0, this.canvas.width, this.canvas.height);
  }
}
